/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Button, Divider, Flex, Tabs} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import GapAnalysisBg from '../../../../assets/skeleton/GapAnalysisBg.png';
import BlockAccessPage from '../../../../components/BlockAccess/BlockAccessPage';
import PageContent from '../../../../components/PageContent/PageContent';
import {useGlobalYear} from '../../../../hooks/useGlobalYear';
import {usePeerUniverse} from '../../../../hooks/usePeerUniverse';
import {
  getGapAnalysis,
  getPeerCompaniesGapAnalysis,
  type GapAnalysisMetricRow,
  type GapAnalysisPillarView,
  type PeerMetricRow
} from '../../../../services/modules/ClimateDB/gapAnalysisAPI';
import Year from '../../../../shared/Year';
import useCompanyStore, {
  accessModule
} from '../../../../store/companyStore/companyDetailsStore';
import {shouldBlockModuleAccess} from '../../../../utils/SubscriptionHelper';
import OverviewInsightPanel, {
  useOverviewInsight
} from '../../../dashboard/Utils/Metrics/OverviewInsightPanel';
import AddPeerCompaniesModal from '../PeerBenchmarking/AddPeerCompaniesModal';

import GapAnalysisSection from './GapAnalysisSection';

const peerSectionsForSelection = (
  pillars: GapAnalysisPillarView[] | null,
  selectedPeerIds: number[],
  selectedNames: string[],
  companyPillars: GapAnalysisPillarView[] | null
): GapAnalysisPillarView[] | null => {
  if (!pillars) {
    return null;
  }

  const averages = new Map<string, string>();

  (companyPillars ?? []).forEach((pillar) => {
    if (pillar.isPeerComparison) {
      return;
    }

    (pillar.datasource as GapAnalysisMetricRow[]).forEach((row) => {
      const text = row.average?.text;

      if (text && text !== '—') {
        averages.set(row.key, text);
        averages.set(row.metric, text);
      }
    });
  });

  const selectedIds = new Set(selectedPeerIds.map((id) => Number(id)));
  const names = new Set(
    selectedNames.map((name) => name.trim().toLowerCase()).filter(Boolean)
  );

  return pillars.map((pillar) => {
    if (!pillar.isPeerComparison) {
      return pillar;
    }

    // The subject (user's own) company is always shown, whatever peers are picked.
    const companies = (pillar.companies ?? []).filter((company) => {
      const name = company.legal_name.trim().toLowerCase();

      return (
        company.is_subject === true ||
        selectedIds.has(Number(company.entity_id)) ||
        names.has(name)
      );
    });

    return {
      ...pillar,
      companies,
      datasource: (pillar.datasource as PeerMetricRow[]).map((row) => ({
        ...row,
        average:
          row.average && row.average !== '—'
            ? row.average
            : averages.get(row.key) ?? averages.get(row.metric) ?? '—'
      }))
    };
  });
};

const GapAnalysisPage: React.FC = () => {
  const {selectedYear, setSelectedYear} = useGlobalYear();
  const [livePillars, setLivePillars] = useState<
    GapAnalysisPillarView[] | null
  >(null);
  const [peerPillars, setPeerPillars] = useState<
    GapAnalysisPillarView[] | null
  >(null);
  const [peerGroupName, setPeerGroupName] = useState<string | null>(null);
  const [isGapLoading, setIsGapLoading] = useState(true);
  const [isPeerLoading, setIsPeerLoading] = useState(true);
  const [isGapUnavailable, setIsGapUnavailable] = useState(false);
  const [isPeerUnavailable, setIsPeerUnavailable] = useState(false);
  const [showAddPeerModal, setShowAddPeerModal] = useState(false);
  const companyId = useCompanyStore((state) => state.companyId);
  const loadKey = `${companyId}|${selectedYear}`;
  const [loadedKey, setLoadedKey] = useState(loadKey);

  if (loadKey !== loadedKey) {
    setLoadedKey(loadKey);
    setIsGapLoading(true);
    setIsPeerLoading(true);
  }
  const userPeerCompanies = useCompanyStore((state) => state.userPeerCompanies);
  const setUserPeerCompanies = useCompanyStore(
    (state) => state.setUserPeerCompanies
  );
  const {
    universe,
    selectedPeerIds: defaultPeerIds,
    isLoading: isUniverseLoading
  } = usePeerUniverse(companyId, selectedYear);
  const selectedPeerIds = useMemo(() => {
    const peers = universe?.peers ?? [];
    const userPeerIds = userPeerCompanies.flatMap((company) => {
      const match = peers.find(
        (peer) => peer.legal_name === company.companyName
      );

      return match ? [match.entity_id] : [];
    });

    // No user-picked peers yet: fall back to the universe defaults, as Peer Benchmarking does.
    return userPeerIds.length > 0 ? userPeerIds : defaultPeerIds;
  }, [universe, userPeerCompanies, defaultPeerIds]);

  const peerCompanyOptions = (universe?.peers ?? []).map((peer) => ({
    value: peer.legal_name,
    label: peer.legal_name,
    entityId: peer.entity_id
  }));
  const gapAccessBlocked = shouldBlockModuleAccess(
    accessModule.Reporting_And_ESG,
    accessModule.gap_analytics
  );

  useEffect(() => {
    let active = true;
    setIsGapLoading(true);

    void getGapAnalysis(companyId, selectedYear).then((result) => {
      if (!active) {
        return;
      }

      setLivePillars(result?.pillars ?? null);
      setPeerGroupName(result?.peerGroupName ?? null);
      setIsGapUnavailable(result === null);
      setIsGapLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyId, selectedYear]);

  useEffect(() => {
    if (isUniverseLoading) {
      return;
    }

    if (selectedPeerIds.length === 0) {
      setPeerPillars(null);
      setIsPeerLoading(false);
      setIsPeerUnavailable(false);

      return;
    }

    let active = true;
    setIsPeerLoading(true);

    void getPeerCompaniesGapAnalysis(
      companyId,
      selectedYear,
      undefined,
      selectedPeerIds
    ).then((result) => {
      if (!active) {
        return;
      }

      setPeerPillars(result?.pillars ?? null);
      if (result?.peerGroupName) {
        setPeerGroupName(result.peerGroupName);
      }
      setIsPeerUnavailable(result === null);
      setIsPeerLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyId, selectedYear, selectedPeerIds, isUniverseLoading]);

  const gapInsight = useOverviewInsight('gap', selectedYear);
  const visiblePeerPillars = useMemo(
    () =>
      peerSectionsForSelection(
        peerPillars,
        selectedPeerIds,
        userPeerCompanies.map((company) => company.companyName),
        livePillars
      ),
    [peerPillars, selectedPeerIds, userPeerCompanies, livePillars]
  );

  return (
    <PageContent>
      {selectedYear && (
        <Flex vertical={false} align="center" justify="space-between">
          <Flex align="center" justify="space-around" gap={5}>
            <div
              style={{
                font: 'normal normal 700 16px Montserrat',
                letterSpacing: '0.56px',
                color: '#282A3A'
              }}
            >
              Gap Analysis
            </div>
            <Flex
              style={{
                padding: '6px 12px',
                backgroundColor: '#F3F4F8',
                borderRadius: '100px',
                font: 'normal normal 500 14px Montserrat',
                height: '32px'
              }}
              gap={5}
            >
              <InfoCircleOutlined />
              Based on BRSR Framework
            </Flex>
            <div
              style={{
                font: 'normal normal 700 16px Montserrat',
                letterSpacing: '0.56px',
                color: '#282A3A'
              }}
            >
              {/* The label stays visible; the name fills in once the API loads. */}
              Industry: {peerGroupName ?? ''}
            </div>
          </Flex>
          {gapAccessBlocked ? null : (
            <Flex align="center" gap={12}>
              <Button
                onClick={() => {
                  setShowAddPeerModal(true);
                }}
              >
                Add your Peer companies
              </Button>
              <Year
                setSelectedYear={setSelectedYear}
                isESG
                colSpan={24}
                noBottomMargin
              />
            </Flex>
          )}
        </Flex>
      )}
      <Divider
        orientationMargin="0"
        className="divider"
        style={{margin: '12px 0px 12px 0px'}}
      />

      {gapInsight.status !== 'hidden' && (
        <div style={{marginBottom: 16}}>
          <OverviewInsightPanel
            status={gapInsight.status}
            result={gapInsight.result}
          />
        </div>
      )}

      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: 'My company',
            children: (
              <>
                {gapAccessBlocked ? (
                  <div style={{height: 800}}>
                    <BlockAccessPage backgroundImage={GapAnalysisBg} />
                  </div>
                ) : (
                  <GapAnalysisSection
                    livePillars={livePillars}
                    isGapLoading={isGapLoading}
                    isGapUnavailable={isGapUnavailable}
                  />
                )}
              </>
            )
          },
          {
            key: '2',
            label: 'Peer companies',
            children: (
              <>
                {gapAccessBlocked ? (
                  <div style={{height: 800}}>
                    <BlockAccessPage backgroundImage={GapAnalysisBg} />
                  </div>
                ) : (
                  <GapAnalysisSection
                    isPeerTab={true}
                    livePillars={visiblePeerPillars}
                    isGapLoading={isPeerLoading}
                    isGapUnavailable={isPeerUnavailable}
                  />
                )}
              </>
            )
          }
        ]}
      />
      <AddPeerCompaniesModal
        open={showAddPeerModal}
        initialCompanies={userPeerCompanies}
        companies={peerCompanyOptions}
        onClose={() => {
          setShowAddPeerModal(false);
        }}
        onSubmit={(companies) => {
          setUserPeerCompanies(companies);
          setShowAddPeerModal(false);
        }}
      />
    </PageContent>
  );
};

export default GapAnalysisPage;
